import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, RefreshCw, ExternalLink, Rocket, Loader2, ShieldPlus, XCircle, AlertTriangle, Info, Smartphone, Tablet, Monitor } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const AUDIT_ICONS = { blocker: XCircle, warning: AlertTriangle, info: Info };
const VIEWPORTS = [
  { key: 'mobile', label: 'Mobile', icon: Smartphone },
  { key: 'tablet', label: 'Tablet', icon: Tablet },
  { key: 'desktop', label: 'Desktop', icon: Monitor },
];

const PublicLaunchAudit = () => {
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.get('/admin/public-launch-audit');
      setAudit(res.data);
      toast[res.data.ready ? 'success' : 'warning'](res.data.verdict);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const toggleViewport = async (vp, done) => {
    await api.put('/admin/launch-responsive', { viewport: vp, done });
    run();
  };

  return (
    <Card className="mb-6 border-[#C9A84C]" data-testid="public-launch-audit">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-serif font-black text-[#1a2744] text-lg">Public Launch Audit</p>
            <p className="text-xs text-slate-500">Runs every automated pre-launch check and gives a final verdict.</p>
          </div>
          <Button onClick={run} disabled={running} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="run-launch-audit-button">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5 text-[#C9A84C]" />} Run Public Launch Audit
          </Button>
        </div>

        {audit && (
          <>
            <div className={`mt-4 rounded-xl p-4 border-2 ${audit.ready ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="launch-audit-verdict">
              <p className={`font-black text-lg ${audit.ready ? 'text-emerald-800' : 'text-red-800'}`}>{audit.verdict}</p>
              <p className="text-xs text-slate-600 mt-1">{audit.blockers} blocker(s) · {audit.warnings} warning(s) · checked just now</p>
            </div>

            {/* Manual responsive confirmations */}
            <div className="flex flex-wrap gap-2 mt-3" data-testid="launch-responsive-toggles">
              {VIEWPORTS.map(({ key, label, icon: Icon }) => {
                const check = audit.checks.find((c) => c.key === `responsive_${key}`);
                const done = check?.pass;
                return (
                  <button key={key} onClick={() => toggleViewport(key, !done)}
                    className={`flex items-center gap-1.5 h-10 px-3 rounded-full text-xs font-bold border ${done ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-white text-slate-600 border-slate-300'}`}
                    data-testid={`responsive-toggle-${key}`}>
                    <Icon className="h-3.5 w-3.5" /> {label} homepage {done ? 'PASS' : 'not confirmed'}
                  </button>
                );
              })}
            </div>

            <div className="space-y-1.5 mt-4" data-testid="launch-audit-checks">
              {audit.checks.map((c) => {
                const Icon = c.pass ? CheckCircle2 : (AUDIT_ICONS[c.severity] || Info);
                const color = c.pass ? 'text-emerald-600' : c.severity === 'blocker' ? 'text-red-600' : c.severity === 'warning' ? 'text-amber-600' : 'text-slate-400';
                return (
                  <div key={c.key} className="flex items-start gap-2.5 border-b border-slate-100 pb-1.5" data-testid={`audit-check-${c.key}`}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${c.pass ? 'text-slate-500' : 'text-[#1a2744]'}`}>{c.label}</p>
                      {c.detail && <p className="text-xs text-slate-400">{c.detail}</p>}
                    </div>
                    {c.link && <Link to={c.link.startsWith('/admin') ? c.link : c.link} className="text-xs font-bold text-[#1a2744] underline underline-offset-4 shrink-0">Open</Link>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminLaunch() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [backupForm, setBackupForm] = useState({ name: '', email: '', password: '' });

  const load = useCallback(() => {
    api.get('/admin/launch-checklist').then((r) => setData(r.data)).catch(() => toast.error('Failed to load checklist'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    if (!data.manual_keys.includes(item.key)) {
      toast.info('This item is checked automatically — follow the hint to complete it.');
      return;
    }
    try {
      await api.put('/admin/launch-checklist', { key: item.key, done: !item.done });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const createBackup = async () => {
    if (!backupForm.email || !backupForm.password) { toast.error('Email and password required'); return; }
    setBusy(true);
    try {
      await api.post('/auth/users', { ...backupForm, role: 'admin' });
      toast.success('Backup admin created — they must change their password on first login');
      setBackupOpen(false);
      setBackupForm({ name: '', email: '', password: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const pct = Math.round((data.done / data.total) * 100);
  const ready = data.done === data.total;

  return (
    <div data-testid="admin-launch">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Rocket className="h-6 w-6 text-[#C9A84C]" /> Launch Readiness</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBackupOpen(true)} className="h-10 font-bold btn-press" data-testid="launch-backup-admin-button"><ShieldPlus className="h-4 w-4 mr-1.5" /> Create Backup Admin</Button>
          <Button variant="outline" onClick={load} className="h-10 font-bold btn-press" data-testid="launch-refresh-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">Complete every item before announcing the site publicly.</p>

      <PublicLaunchAudit />

      <Card className={`mb-6 ${ready ? 'border-emerald-300 bg-emerald-50' : ''}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-[#1a2744]">{ready ? 'Ready to launch!' : 'Launch progress'}</p>
            <p className="font-black text-[#1a2744]" data-testid="launch-progress-count">{data.done} / {data.total}</p>
          </div>
          <Progress value={pct} className="h-3" />
        </CardContent>
      </Card>

      <div className="space-y-2">
        {data.items.map((item) => {
          const manual = data.manual_keys.includes(item.key);
          return (
            <Card key={item.key} className={item.done ? 'border-emerald-200' : ''} data-testid={`launch-item-${item.key}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <button
                  onClick={() => toggle(item)}
                  className={`mt-0.5 shrink-0 ${manual ? 'cursor-pointer' : 'cursor-default'}`}
                  aria-label={item.done ? 'Done' : 'Not done'}
                  data-testid={`launch-toggle-${item.key}`}
                >
                  {item.done ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <Circle className={`h-6 w-6 ${manual ? 'text-[#C9A84C]' : 'text-slate-300'}`} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold ${item.done ? 'text-slate-400 line-through' : 'text-[#1a2744]'}`}>{item.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.hint} {!manual && <span className="font-bold text-slate-400">(checked automatically)</span>}{manual && <span className="font-bold text-[#C9A84C]">(tap the circle to tick manually)</span>}</p>
                </div>
                {item.link && (
                  <Button asChild variant="outline" size="sm" className="font-bold shrink-0">
                    {item.link.startsWith('/admin') ? <Link to={item.link}>Open</Link> : <a href={item.link} target="_blank" rel="noopener noreferrer">Open <ExternalLink className="h-3 w-3 ml-1" /></a>}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={backupOpen} onOpenChange={setBackupOpen}>
        <DialogContent data-testid="backup-admin-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Create backup admin</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A second admin account keeps the campaign from being locked out. They will be required to change this password on first login.</p>
          <div className="space-y-3">
            <Input value={backupForm.name} onChange={(e) => setBackupForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className="h-11" data-testid="backup-name-input" />
            <Input type="email" value={backupForm.email} onChange={(e) => setBackupForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="h-11" data-testid="backup-email-input" />
            <Input type="password" value={backupForm.password} onChange={(e) => setBackupForm((f) => ({ ...f, password: e.target.value }))} placeholder="Temporary password (min 8 chars)" className="h-11" data-testid="backup-password-input" />
            <Button onClick={createBackup} disabled={busy} className="w-full h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="backup-create-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Create Backup Admin
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
